import {
  ArrowLeft,
  Check,
  Copy,
  FileText,
  X,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useState,
} from 'react'

type ScriptId =
  | 'cancelled-call'
  | 'opener-to-audit'
  | 'talk-to-wife'
  | 'enough-leads'
  | 'guarantee-results'
  | 'burned-by-agency'
  | 'already-have-someone'

type ScriptCategory =
  | 'follow-up'
  | 'objection'

type ScriptDefinition = {
  id: ScriptId
  category: ScriptCategory
  title: string
  description: string
  motivation: string
  requiredFields: Array<'name' | 'company'>
  build: (values: {
    name: string
    company: string
  }) => string
}

const SCRIPTS: ScriptDefinition[] = [
  {
    id: 'cancelled-call',
    category: 'follow-up',
    title: 'Cancelled Call',
    description:
      'Follow up after a prospect cancels a call.',
    motivation:
      'Get that conversation back on the calendar.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name} quick one

I saw you had to cancel our call, totally get it, things come up.

But let me ask you something... are you still looking to grow your agency & need a white label growth partner?

Let's get you rescheduled so I can show you exactly what's working in your space and how we can apply it to your business.

What's your availability like this week?`,
  },

  {
    id: 'opener-to-audit',
    category: 'follow-up',
    title: 'Opener to Audit',
    description:
      'Open a conversation around SEO and AI visibility.',
    motivation:
      'Lead with value. Earn the next reply.',
    requiredFields: [],
    build: () => `Quick question, are you currently doing anything to consistently dominate Google or show up in AI-driven search for your dumpster rental business?

Because I specialize in helping dumpster rental companies lock down their local market using SEO and AI positioning and there's usually a lot of missed opportunity I can spot fast.

If you're open to it, I can put together a quick game plan + audit you can implement yourself.

Worth taking a look?`,
  },

  {
    id: 'talk-to-wife',
    category: 'follow-up',
    title: 'Had to Talk to His Wife',
    description:
      'Follow up after the prospect needs to discuss the offer with his wife.',
    motivation:
      'Keep it calm. Move the conversation forward.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name} I really enjoyed our call, man. I've been thinking about your setup and there's a serious opportunity here if we move on this the right way.

Totally respect you wanting to run it by your wife, that's the smart move.

Out of curiosity, what part of the plan do you think she's going to have the most questions about? That way I can make sure you've got everything covered when you talk with her.

Also, if it makes things easier, I'm happy to jump on a quick call with both of you so I can walk through the game plan and answer everything directly.

Either way, let's get you dialed in. When's a good time to reconnect?`,
  },

  {
    id: 'enough-leads',
    category: 'objection',
    title: 'We Already Get Enough Leads',
    description:
      'Reframe the conversation from lead volume to lead quality.',
    motivation:
      'Shift the conversation from more leads to better opportunities.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name}, totally fair. If you're already getting plenty of leads, I wouldn't tell you that you need more just for the sake of having more.

What I'd be curious about is whether they're the right leads.

Are you consistently getting the higher-value jobs you actually want, in the areas you want to dominate, or are you still taking whatever comes through?

Because if lead volume is already handled, the opportunity might be improving quality, margins, and market position instead of chasing more volume.

Worth taking a quick look at that?`,
  },

  {
    id: 'guarantee-results',
    category: 'objection',
    title: 'Can You Guarantee Results?',
    description:
      'Answer guarantee questions without making fake promises.',
    motivation:
      'Credibility beats promises you cannot control.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name}, I wouldn't trust anyone who guarantees you a specific ranking, number of leads, or revenue before they've even looked at the business.

There are too many variables nobody honestly controls.

What I can show you is exactly what we'd work on, what opportunities we see, how progress gets measured, and whether the numbers are moving in the right direction.

I'd rather give you something measurable than sell you a guarantee that sounds good on a call.

Want me to show you what I'd actually be looking at for your business?`,
  },

  {
    id: 'burned-by-agency',
    category: 'objection',
    title: 'Burned by an Agency Before',
    description:
      'Handle skepticism after a bad experience with another agency.',
    motivation:
      'Do not ask for trust. Earn it.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name}, then I don't blame you for being cautious.

If somebody took your money, overpromised, disappeared, or left you wondering what you were even paying for, another agency telling you "trust us" probably means nothing.

So I'm not asking you to trust us upfront.

Let me earn that part.

I'd rather show you what I see, what I'd change, why I'd change it, and what we'd actually measure before you make any decision.

What was the biggest thing the last agency did that made you say, "I'm never doing this again"?`,
  },

  {
    id: 'already-have-someone',
    category: 'objection',
    title: 'We Already Have Someone',
    description:
      'Position yourself as a second set of eyes without attacking their provider.',
    motivation:
      'Respect the relationship. Find the gap.',
    requiredFields: ['name'],
    build: ({ name }) => `Hey ${name}, that's actually a good thing. If you already have someone working on it, I'm not going to tell you to fire them just so I can get your business.

I'm curious though, what are they mainly focused on for you right now?

SEO? Website? Google Maps? Paid ads? AI search?

If they've got everything covered and it's performing, great.

But if there are gaps, I'm happy to give you a second set of eyes and show you what I'm seeing. No pressure to change anything.

Want me to take a look?`,
  },
]

type ScriptModalProps = {
  onClose: () => void
}

export function ScriptModal({
  onClose,
}: ScriptModalProps) {
  const [selectedId, setSelectedId] =
    useState<ScriptId | null>(null)

  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [copySuccess, setCopySuccess] =
    useState(false)

  const selected = useMemo(
    () =>
      SCRIPTS.find(
        (script) => script.id === selectedId,
      ) ?? null,
    [selectedId],
  )

  const followUpScripts = useMemo(
    () =>
      SCRIPTS.filter(
        (script) =>
          script.category === 'follow-up',
      ),
    [],
  )

  const objectionScripts = useMemo(
    () =>
      SCRIPTS.filter(
        (script) =>
          script.category === 'objection',
      ),
    [],
  )

  const generatedScript = useMemo(() => {
    if (!selected) return ''

    return selected.build({
      name: name.trim() || '[Name]',
      company: company.trim() || '[Company]',
    })
  }, [selected, name, company])

  const missingRequiredField =
    selected?.requiredFields.some((field) => {
      if (field === 'name') return !name.trim()
      if (field === 'company') {
        return !company.trim()
      }

      return false
    }) ?? false

  useEffect(() => {
    function handleEscape(
      event: KeyboardEvent,
    ) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener(
      'keydown',
      handleEscape,
    )

    return () =>
      window.removeEventListener(
        'keydown',
        handleEscape,
      )
  }, [onClose])

  useEffect(() => {
    if (!copySuccess) return

    const timer = window.setTimeout(() => {
      onClose()
    }, 1900)

    return () =>
      window.clearTimeout(timer)
  }, [copySuccess, onClose])

  async function copyScript() {
    if (
      !generatedScript ||
      missingRequiredField
    ) {
      return
    }

    try {
      await navigator.clipboard.writeText(
        generatedScript,
      )
    } catch {
      const textarea =
        document.createElement('textarea')

      textarea.value = generatedScript
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'

      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }

    setCopySuccess(true)
  }

  function selectScript(id: ScriptId) {
    setSelectedId(id)
    setName('')
    setCompany('')
  }

  function goBack() {
    setSelectedId(null)
    setName('')
    setCompany('')
  }

  function renderScriptCard(
    script: ScriptDefinition,
  ) {
    return (
      <button
        className="script-template-card"
        key={script.id}
        onClick={() =>
          selectScript(script.id)
        }
        type="button"
      >
        <span className="script-template-icon">
          <FileText size={20} />
        </span>

        <span className="script-template-copy">
          <strong>
            {script.title}
          </strong>

          <small>
            {script.description}
          </small>
        </span>

        <span
          aria-hidden="true"
          className="script-template-arrow"
        >
          ›
        </span>
      </button>
    )
  }

  if (copySuccess && selected) {
    return (
      <div className="script-modal-backdrop script-success-backdrop">
        <div
          aria-live="polite"
          className="script-copy-success"
          role="status"
        >
          <span className="script-copy-success-icon">
            <Check size={30} />
          </span>

          <strong>
            Copied, William.
          </strong>

          <p>
            {selected.motivation}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="script-modal-backdrop"
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget
        ) {
          onClose()
        }
      }}
    >
      <section
        aria-labelledby="script-modal-title"
        aria-modal="true"
        className="script-modal"
        role="dialog"
      >
        <header className="script-modal-header">
          <div>
            <span className="script-modal-kicker">
              QUICK ACTION
            </span>

            <h2 id="script-modal-title">
              SCRIPT
            </h2>

            <p>
              Choose an approved DM script and
              personalize only what it needs.
            </p>
          </div>

          <button
            aria-label="Close Script"
            className="script-modal-close"
            onClick={onClose}
            type="button"
          >
            <X size={21} />
          </button>
        </header>

        {!selected ? (
          <div className="script-library">
            <div className="script-library-heading">
              <span>
                DM SCRIPT LIBRARY
              </span>

              <h3>
                Choose a script below,{' '}
                <em>William.</em>
              </h3>
            </div>

            <section className="script-category">
              <div className="script-category-heading">
                <span>
                  FOLLOW-UP & OUTREACH
                </span>

                <small>
                  Continue or reopen the conversation.
                </small>
              </div>

              <div className="script-template-list">
                {followUpScripts.map(
                  renderScriptCard,
                )}
              </div>
            </section>

            <section className="script-category">
              <div className="script-category-heading">
                <span>
                  OBJECTION RESPONSES
                </span>

                <small>
                  Handle resistance without sounding pushy.
                </small>
              </div>

              <div className="script-template-list">
                {objectionScripts.map(
                  renderScriptCard,
                )}
              </div>
            </section>
          </div>
        ) : (
          <div className="script-builder">
            <div className="script-builder-controls">
              <button
                className="script-back-button"
                onClick={goBack}
                type="button"
              >
                <ArrowLeft size={16} />
                Back to scripts
              </button>

              <div className="script-selected-heading">
                <span>
                  SELECTED SCRIPT
                </span>

                <h3>
                  {selected.title}
                </h3>

                <p>
                  {selected.description}
                </p>
              </div>

              {selected.requiredFields.length >
              0 ? (
                <div className="script-fields">
                  <div className="script-fields-heading">
                    <span>
                      PERSONALIZE
                    </span>

                    <p>
                      Complete only what this
                      script needs.
                    </p>
                  </div>

                  {selected.requiredFields.includes(
                    'name',
                  ) ? (
                    <label className="script-field">
                      <span>
                        Name <b>*</b>
                      </span>

                      <input
                        autoComplete="off"
                        autoFocus
                        onChange={(event) =>
                          setName(
                            event.target.value,
                          )
                        }
                        placeholder="Example: Blake"
                        type="text"
                        value={name}
                      />
                    </label>
                  ) : null}

                  {selected.requiredFields.includes(
                    'company',
                  ) ? (
                    <label className="script-field">
                      <span>
                        Company <b>*</b>
                      </span>

                      <input
                        autoComplete="off"
                        onChange={(event) =>
                          setCompany(
                            event.target.value,
                          )
                        }
                        placeholder="Example: 1in5 Junk Removal"
                        type="text"
                        value={company}
                      />
                    </label>
                  ) : null}
                </div>
              ) : (
                <div className="script-no-fields">
                  No personalization required.
                </div>
              )}
            </div>

            <div className="script-preview-section">
              <div className="script-preview-heading">
                <span>
                  YOUR SCRIPT
                </span>

                <p>
                  Ready to paste into Messenger.
                </p>
              </div>

              <div className="script-preview">
                {generatedScript}
              </div>

              <button
                className="script-copy-button"
                disabled={
                  missingRequiredField
                }
                onClick={copyScript}
                type="button"
              >
                <Copy size={18} />
                Copy Script
              </button>

              {missingRequiredField ? (
                <p className="script-required-hint">
                  Enter the required
                  information first.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
